import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface TestStatusProps {
  enableDarkTheme: boolean;
  processingStatus?: {
    brakes: string;
    chassis: string;
    engine: string;
  };
  brakeBreachCount?: number;
  chassisBreachCount?: number;
  engineBreachCount?: number;
  onAnalysisClick?: (section: string) => void;
  selectedAnalysisSection?: string;
}

const TestStatus = ({
  enableDarkTheme,
  processingStatus,
  brakeBreachCount,
  chassisBreachCount,
  engineBreachCount,
  onAnalysisClick,
  selectedAnalysisSection,
}: TestStatusProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return '#ffc107'; // Yellow for processing
      case 'completed':
        return '#4caf50'; // Green for completed
      case 'error':
        return '#f44336'; // Red for error
      default:
        return '#9e9e9e'; // Gray for idle
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Ready';
      case 'error':
        return 'Error';
      default:
        return 'Idle';
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        marginTop: '45%',
        width: '100%',
        padding: '5%',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: enableDarkTheme ? 'white' : 'black',
          fontWeight: 600,
          marginBottom: '8px',
          textAlign: 'center',
          fontSize: '16px',
        }}
      >
        {t('test_status')}
      </Typography>
      <Typography
        sx={{
          color: enableDarkTheme ? 'white' : 'black',
          fontWeight: 400,
          marginBottom: '16px',
          textAlign: 'center',
          fontSize: '13px',
          fontStyle: 'italic',
        }}
      >
        {t('last_24_hours')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor:
              selectedAnalysisSection === 'brakes'
                ? 'rgba(74, 236, 74, 0.1)'
                : `rgba(${getStatusColor(
                    processingStatus?.brakes ?? 'idle'
                  ).replace('#', '')}, 0.2)`,
            borderRadius: '8px',
            border: `1px solid ${getStatusColor(
              processingStatus?.brakes ?? 'idle'
            )}40`,
            cursor: 'pointer',
            minHeight: '40px',
          }}
          onClick={() => onAnalysisClick?.('brakes')}
        >
          <Box>
            <Typography
              sx={{
                color: enableDarkTheme ? 'white' : 'black',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('brakes_struts')}
            </Typography>

            {selectedAnalysisSection === 'brakes' ? null : (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.8)',
                  cursor: 'pointer',
                  '&:hover': {
                    color: palette.primary,
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('click_to_view_analysis')}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(
                  processingStatus?.brakes ?? 'idle'
                ),
                animation:
                  processingStatus?.brakes === 'processing'
                    ? 'pulse 1.5s infinite'
                    : 'none',
              }}
            />
            <Typography
              sx={{
                color: getStatusColor(processingStatus?.brakes ?? 'idle'),
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {getStatusText(processingStatus?.brakes ?? 'idle')}
            </Typography>
            {selectedAnalysisSection === 'brakes' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  color: enableDarkTheme ? 'white' : 'black',
                  fontSize: '16px',
                }}
              >
                ▶
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor:
              selectedAnalysisSection === 'chassis'
                ? 'rgba(74, 236, 74, 0.1)'
                : `rgba(${getStatusColor(
                    processingStatus?.chassis ?? 'idle'
                  ).replace('#', '')}, 0.2)`,
            borderRadius: '8px',
            border: `1px solid ${getStatusColor(
              processingStatus?.chassis ?? 'idle'
            )}40`,
            cursor: 'pointer',
            minHeight: '40px',
          }}
          onClick={() => onAnalysisClick?.('chassis')}
        >
          <Box>
            <Typography
              sx={{
                color: enableDarkTheme ? 'white' : 'black',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('chassis_info')}
            </Typography>

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.8)',
                cursor: 'pointer',
                '&:hover': {
                  color: palette.primary,
                  textDecoration: 'underline',
                },
              }}
              onClick={() => onAnalysisClick?.('chassis')}
            >
              {t('click_to_view_analysis')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(
                  processingStatus?.chassis ?? 'idle'
                ),
                animation:
                  processingStatus?.chassis === 'processing'
                    ? 'pulse 1.5s infinite'
                    : 'none',
              }}
            />
            <Typography
              sx={{
                color: getStatusColor(processingStatus?.chassis ?? 'idle'),
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {getStatusText(processingStatus?.chassis ?? 'idle')}
            </Typography>
            {selectedAnalysisSection === 'chassis' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  color: enableDarkTheme ? 'white' : 'black',
                  fontSize: '16px',
                }}
              >
                ▶
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor:
              selectedAnalysisSection === 'engine'
                ? 'rgba(74, 236, 74, 0.1)'
                : `rgba(${getStatusColor(
                    processingStatus?.engine ?? 'idle'
                  ).replace('#', '')}, 0.2)`,
            borderRadius: '8px',
            border: `1px solid ${getStatusColor(
              processingStatus?.engine ?? 'idle'
            )}40`,
            cursor: 'pointer',
            minHeight: '40px',
          }}
          onClick={() => onAnalysisClick?.('engine')}
        >
          <Box>
            <Typography
              sx={{
                color: enableDarkTheme ? 'white' : 'black',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('engine_params')}
            </Typography>

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.8)',
                cursor: 'pointer',
                '&:hover': {
                  color: palette.primary,
                  textDecoration: 'underline',
                },
              }}
              onClick={() => onAnalysisClick?.('engine')}
            >
              {t('click_to_view_analysis')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(
                  processingStatus?.engine ?? 'idle'
                ),
                animation:
                  processingStatus?.engine === 'processing'
                    ? 'pulse 1.5s infinite'
                    : 'none',
              }}
            />
            <Typography
              sx={{
                color: getStatusColor(processingStatus?.engine ?? 'idle'),
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {getStatusText(processingStatus?.engine ?? 'idle')}
            </Typography>
            {selectedAnalysisSection === 'engine' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  color: enableDarkTheme ? 'white' : 'black',
                  fontSize: '16px',
                }}
              >
                ▶
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TestStatus;
